import React, { useState } from 'react'
import {
  Row,
  Col,
  Card,
  Badge,
  ProgressBar,
  Spinner,
  Alert,
  Table,
  Modal,
  Button,
} from 'react-bootstrap'
import { OverallBehavioralProfile } from 'interfaces/behavioral-profile'

interface BehavioralProfileOverviewProps {
  profile: OverallBehavioralProfile | null
  loading: boolean
  error: Error | null
  systemUser: string
}

const BehavioralProfileOverview: React.FC<BehavioralProfileOverviewProps> = ({
  profile,
  loading,
  error,
  systemUser,
}) => {
  const [showInfoModal, setShowInfoModal] = useState(false)
  if (loading) {
    return (
      <Card className="mb-4">
        <Card.Header>
          <h5 className="mb-0">Panopticon Profile</h5>
        </Card.Header>
        <Card.Body>
          <div className="text-center p-4">
            <Spinner animation="border" role="status">
              <span className="visually-hidden">Loading profile...</span>
            </Spinner>
            <div className="mt-2">Loading behavioral profile...</div>
          </div>
        </Card.Body>
      </Card>
    )
  }

  if (error) {
    return (
      <Card className="mb-4">
        <Card.Header>
          <h5 className="mb-0">Panopticon Profile</h5>
        </Card.Header>
        <Card.Body>
          <Alert variant="warning">
            <Alert.Heading>Profile Not Available</Alert.Heading>
            <p>
              Unable to load behavioral profile data. This user may not have
              sufficient message history for analysis.
            </p>
          </Alert>
        </Card.Body>
      </Card>
    )
  }

  if (!profile) {
    return (
      <Card className="mb-4">
        <Card.Header>
          <h5 className="mb-0">Panopticon Profile</h5>
        </Card.Header>
        <Card.Body>
          <Alert variant="info">
            <Alert.Heading>No Profile Data</Alert.Heading>
            <p>
              This user doesn't have behavioral profile data yet. Profiles are
              created after message analysis by the Panopticon system.
            </p>
          </Alert>
        </Card.Body>
      </Card>
    )
  }

  // Debug logging to see what data we have (remove for production)
  // console.log('Behavioral Profile Data:', profile)

  // Helper function to get quality tier color
  const getQualityTierColor = (tier: string) => {
    switch (tier) {
      case 'EXCELLENT':
        return 'success'
      case 'GOOD':
        return 'primary'
      case 'FAIR':
        return 'warning'
      case 'POOR':
        return 'danger'
      case 'MASS_MARKETING':
        return 'dark'
      default:
        return 'secondary'
    }
  }

  // Helper function to get risk level color
  const getRiskLevelColor = (score: number) => {
    if (score >= 80) return 'danger'
    if (score >= 60) return 'warning'
    if (score >= 40) return 'info'
    return 'success'
  }

  return (
    <>
      <Card
        className="mb-4 bg-dark text-white border-secondary shadow-lg"
        style={{ borderRadius: '12px' }}
      >
        <Card.Header
          className="d-flex justify-content-between align-items-center bg-dark border-secondary"
          style={{
            borderRadius: '12px 12px 0 0',
            background: 'linear-gradient(135deg, #2d3748 0%, #1a202c 100%)',
            borderBottom: '2px solid #4a5568',
          }}
        >
          <div className="d-flex align-items-center gap-3">
            <h5 className="mb-0 text-white fw-bold">Panopticon Profile</h5>
            <Button
              variant="outline-info"
              size="sm"
              className="ms-2 rounded-circle"
              style={{
                width: '32px',
                height: '32px',
                padding: '0',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
              onClick={() => setShowInfoModal(true)}
            >
              <span style={{ fontSize: '14px', fontWeight: 'bold' }}>i</span>
            </Button>
          </div>
          <div className="d-flex gap-3 align-items-center">
            <Badge
              variant={getQualityTierColor(profile.qualityTier)}
              className="px-3 py-2"
              style={{
                fontSize: '0.85em',
                borderRadius: '8px',
                fontWeight: '600',
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
              }}
            >
              {profile.qualityTier.replace('_', ' ')}
            </Badge>
            <div className="text-end">
              <small
                className="text-muted d-block"
                style={{ fontSize: '0.75em' }}
              >
                Last Updated
              </small>
              <div className="d-flex align-items-center justify-content-end gap-2">
                <small
                  className="text-info fw-medium"
                  style={{ fontSize: '0.8em' }}
                >
                  {new Date(profile.metadata.fetchedAt).toLocaleString()}
                </small>
                <Button
                  variant="outline-info"
                  size="sm"
                  className="p-1"
                  style={{
                    width: '24px',
                    height: '24px',
                    fontSize: '12px',
                    lineHeight: '1',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    borderRadius: '4px',
                  }}
                  onClick={() => window.location.reload()}
                  title="Refresh profile data"
                >
                  ↻
                </Button>
              </div>
            </div>
          </div>
        </Card.Header>
        <Card.Body className="bg-dark">
          {/* Key Metrics Row */}
          <Row className="mb-4">
            <Col md={4} className="mb-3">
              <Card
                className="h-100 border-2 text-white shadow-lg position-relative overflow-hidden"
                style={{
                  borderRadius: '12px',
                  background:
                    'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  border: '2px solid rgba(102, 126, 234, 0.3)',
                  transition: 'all 0.3s ease',
                }}
              >
                <Card.Body className="text-center p-4 position-relative">
                  <div
                    className="display-5 fw-bold mb-2"
                    style={{ color: '#ffffff' }}
                  >
                    {profile.totalMessages
                      ? profile.totalMessages.toLocaleString()
                      : '-'}
                  </div>
                  <div
                    className="fw-semibold mb-2"
                    style={{ color: '#ffffff' }}
                  >
                    Total Messages
                  </div>
                  <div className="d-flex justify-content-center gap-3">
                    <small style={{ fontSize: '0.8em', color: '#ffffff' }}>
                      ⬇️ {profile.totalInboundMessages || '-'}
                    </small>
                    <small style={{ fontSize: '0.8em', color: '#ffffff' }}>
                      ⬆️ {profile.totalOutboundMessages || '-'}
                    </small>
                  </div>
                </Card.Body>
              </Card>
            </Col>
            <Col md={4} className="mb-3">
              <Card
                className="h-100 border-2 text-white shadow-lg position-relative overflow-hidden"
                style={{
                  borderRadius: '12px',
                  background:
                    'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                  border: '2px solid rgba(16, 185, 129, 0.3)',
                  transition: 'all 0.3s ease',
                }}
              >
                <Card.Body className="text-center p-4 position-relative">
                  <div
                    className="display-5 fw-bold mb-2"
                    style={{ color: '#ffffff' }}
                  >
                    {profile.avgQualityScore != null
                      ? profile.avgQualityScore.toFixed(1)
                      : '-'}
                  </div>
                  <div
                    className="fw-semibold mb-2"
                    style={{ color: '#ffffff' }}
                  >
                    Avg Quality Score
                  </div>
                  <ProgressBar
                    now={profile.avgQualityScore}
                    className="mt-2"
                    style={{
                      height: '6px',
                      borderRadius: '3px',
                      backgroundColor: 'rgba(255,255,255,0.2)',
                    }}
                  >
                    <ProgressBar
                      now={profile.avgQualityScore}
                      style={{
                        backgroundColor: '#fff',
                        borderRadius: '3px',
                      }}
                    />
                  </ProgressBar>
                </Card.Body>
              </Card>
            </Col>
            <Col md={4} className="mb-3">
              <Card
                className="h-100 border-2 text-white shadow-lg position-relative overflow-hidden"
                style={{
                  borderRadius: '12px',
                  background:
                    profile.overallRiskScore >= 80
                      ? 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)'
                      : profile.overallRiskScore >= 60
                      ? 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)'
                      : 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                  border: `2px solid rgba(${
                    profile.overallRiskScore >= 80
                      ? '239, 68, 68'
                      : profile.overallRiskScore >= 60
                      ? '245, 158, 11'
                      : '16, 185, 129'
                  }, 0.3)`,
                  transition: 'all 0.3s ease',
                }}
              >
                <Card.Body className="text-center p-4 position-relative">
                  <div
                    className="display-5 fw-bold mb-2"
                    style={{ color: '#ffffff' }}
                  >
                    {profile.overallRiskScore != null
                      ? profile.overallRiskScore.toFixed(1)
                      : '-'}
                  </div>
                  <div
                    className="fw-semibold mb-2"
                    style={{ color: '#ffffff' }}
                  >
                    Overall Risk Score
                  </div>
                  <ProgressBar
                    now={profile.overallRiskScore}
                    className="mt-2"
                    style={{
                      height: '6px',
                      borderRadius: '3px',
                      backgroundColor: 'rgba(255,255,255,0.2)',
                    }}
                  >
                    <ProgressBar
                      now={profile.overallRiskScore}
                      style={{
                        backgroundColor: '#fff',
                        borderRadius: '3px',
                      }}
                    />
                  </ProgressBar>
                </Card.Body>
              </Card>
            </Col>
          </Row>

          {/* Additional Metrics Row */}
          <Row className="mb-4">
            <Col md={6} className="mb-3">
              <Card
                className="h-100 border-2 text-white shadow-lg position-relative overflow-hidden"
                style={{
                  borderRadius: '12px',
                  background:
                    'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
                  border: '2px solid rgba(59, 130, 246, 0.3)',
                  transition: 'all 0.3s ease',
                }}
              >
                <Card.Body className="text-center p-4 position-relative">
                  <div
                    className="display-5 fw-bold mb-2"
                    style={{ color: '#ffffff' }}
                  >
                    {profile.avgConversationScore != null
                      ? profile.avgConversationScore.toFixed(1)
                      : '-'}
                  </div>
                  <div
                    className="fw-semibold mb-2"
                    style={{ color: '#ffffff' }}
                  >
                    Avg Conversation Score
                  </div>
                  <ProgressBar
                    now={profile.avgConversationScore}
                    className="mt-2"
                    style={{
                      height: '6px',
                      borderRadius: '3px',
                      backgroundColor: 'rgba(255,255,255,0.2)',
                    }}
                  >
                    <ProgressBar
                      now={profile.avgConversationScore}
                      style={{
                        backgroundColor: '#fff',
                        borderRadius: '3px',
                      }}
                    />
                  </ProgressBar>
                </Card.Body>
              </Card>
            </Col>
            <Col md={6} className="mb-3">
              <Card
                className="h-100 border-2 text-white shadow-lg position-relative overflow-hidden"
                style={{
                  borderRadius: '12px',
                  background:
                    'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                  border: '2px solid rgba(245, 158, 11, 0.3)',
                  transition: 'all 0.3s ease',
                }}
              >
                <Card.Body className="text-center p-4 position-relative">
                  <div
                    className="display-5 fw-bold mb-2"
                    style={{ color: '#ffffff' }}
                  >
                    {profile.avgSpamLikelihood != null
                      ? profile.avgSpamLikelihood.toFixed(1)
                      : '-'}
                  </div>
                  <div
                    className="fw-semibold mb-2"
                    style={{ color: '#ffffff' }}
                  >
                    Avg Spam Likelihood
                  </div>
                  <ProgressBar
                    now={profile.avgSpamLikelihood}
                    className="mt-2"
                    style={{
                      height: '6px',
                      borderRadius: '3px',
                      backgroundColor: 'rgba(255,255,255,0.2)',
                    }}
                  >
                    <ProgressBar
                      now={profile.avgSpamLikelihood}
                      style={{
                        backgroundColor: '#fff',
                        borderRadius: '3px',
                      }}
                    />
                  </ProgressBar>
                </Card.Body>
              </Card>
            </Col>
          </Row>

          {/* Message Categories */}
          <Row className="mb-4">
            <Col md={12} className="mb-3">
              <Card className="border border-light bg-dark text-white shadow-lg">
                <Card.Header className="bg-dark border-bottom border-light">
                  <h6 className="text-white mb-0 fw-bold">
                    Message Categories
                  </h6>
                </Card.Header>
                <Card.Body className="p-4 bg-dark">
                  {profile.messagesByCategory &&
                  Object.keys(profile.messagesByCategory).length > 0 ? (
                    <Row className="g-3">
                      {Object.entries(profile.messagesByCategory)
                        .sort(([, a], [, b]) => b - a)
                        .map(([category, count]) => (
                          <Col md={4} key={category}>
                            <div
                              className="text-center p-3 rounded"
                              style={{
                                backgroundColor: 'rgba(255,255,255,0.05)',
                                border: '1px solid rgba(255,255,255,0.1)',
                              }}
                            >
                              <div className="text-white fs-5 fw-bold mb-1">
                                {count}
                              </div>
                              <div className="text-white-50 small">
                                {category
                                  .replace(/_/g, ' ')
                                  .toLowerCase()
                                  .replace(/\b\w/g, (l) => l.toUpperCase())}
                              </div>
                            </div>
                          </Col>
                        ))}
                    </Row>
                  ) : (
                    <div className="text-center p-4">
                      <span className="text-white-50">
                        No message category data available
                      </span>
                    </div>
                  )}
                </Card.Body>
              </Card>
            </Col>
            <Col md={12} className="mb-3">
              <Card className="border border-light bg-dark text-white shadow-lg">
                <Card.Header className="bg-dark border-bottom border-light">
                  <h6 className="text-white mb-0 fw-bold">
                    First Message Analytics
                  </h6>
                </Card.Header>
                <Card.Body className="p-4 bg-dark">
                  {profile.firstMessageAnalytics &&
                  Object.keys(profile.firstMessageAnalytics).length > 0 ? (
                    <Row className="g-3">
                      {Object.entries(profile.firstMessageAnalytics)
                        .filter(
                          ([key, value]) =>
                            value !== null && value !== undefined
                        )
                        .filter(([key]) => !key.toLowerCase().includes('total'))
                        .map(([key, value]) => (
                          <Col md={3} key={key}>
                            <div
                              className="text-center p-3 rounded"
                              style={{
                                backgroundColor: 'rgba(255,255,255,0.05)',
                                border: '1px solid rgba(255,255,255,0.1)',
                              }}
                            >
                              <div className="text-white fs-5 fw-bold mb-1">
                                {value}
                              </div>
                              <div className="text-white-50 small">
                                {key
                                  .replace(/([A-Z])/g, ' $1')
                                  .replace(/^./, (str) => str.toUpperCase())
                                  .trim()}
                              </div>
                            </div>
                          </Col>
                        ))}
                    </Row>
                  ) : (
                    <div className="text-center p-4">
                      <span className="text-white-50">
                        No first message analytics available
                      </span>
                    </div>
                  )}
                </Card.Body>
              </Card>
            </Col>
          </Row>

          {/* Data Source Info */}
          <Row className="mt-3">
            <Col>
              <small className="text-muted">
                Data source: {profile.metadata.source} |{' '}
                {profile.metadata.lastUpdated && (
                  <>
                    Last updated:{' '}
                    {new Date(profile.metadata.lastUpdated).toLocaleString()} |{' '}
                  </>
                )}
                Linq User: {profile.systemUser}
              </small>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* Field Information Modal */}
      <Modal
        show={showInfoModal}
        onHide={() => setShowInfoModal(false)}
        size="xl"
        centered
        backdrop
      >
        <Modal.Header
          className="bg-dark text-white border-secondary d-flex justify-content-between align-items-center"
          style={{
            background: 'linear-gradient(135deg, #2d3748 0%, #1a202c 100%)',
            borderBottom: '2px solid #4a5568',
          }}
        >
          <Modal.Title>
            <div>
              <div className="fw-bold">Metrics Documentation</div>
              <small className="text-muted">
                Understanding behavioral analysis metrics
              </small>
            </div>
          </Modal.Title>
          <Button
            variant="link"
            className="p-0 border-0"
            onClick={() => setShowInfoModal(false)}
            style={{
              color: '#dc3545',
              fontSize: '24px',
              lineHeight: '1',
              textDecoration: 'none',
            }}
          >
            ×
          </Button>
        </Modal.Header>

        <Modal.Body
          className="bg-dark text-white p-4"
          style={{ maxHeight: '70vh', overflowY: 'auto' }}
        >
          {/* Message Categories */}
          <Card className="border border-secondary bg-secondary text-white mb-3">
            <Card.Header className="bg-secondary border-bottom border-secondary">
              <h6 className="text-light mb-0 fw-semibold">
                Message Categories
              </h6>
            </Card.Header>
            <Card.Body className="p-3">
              <Row className="g-2">
                <Col md={4}>
                  <div className="mb-2">
                    <span className="fw-bold text-white">Personal:</span>{' '}
                    <small className="text-light">
                      Personal communications and responses
                    </small>
                  </div>
                  <div className="mb-2">
                    <span className="fw-bold text-white">
                      Business Response:
                    </span>{' '}
                    <small className="text-light">
                      Professional business replies
                    </small>
                  </div>
                  <div className="mb-2">
                    <span className="fw-bold text-white">
                      Positive Response:
                    </span>{' '}
                    <small className="text-light">
                      Favorable customer replies
                    </small>
                  </div>
                </Col>
                <Col md={4}>
                  <div className="mb-2">
                    <span className="fw-bold text-white">
                      Neutral Response:
                    </span>{' '}
                    <small className="text-light">
                      Neither positive nor negative replies
                    </small>
                  </div>
                  <div className="mb-2">
                    <span className="fw-bold text-white">Promotional:</span>{' '}
                    <small className="text-light">
                      Marketing and sales messages
                    </small>
                  </div>
                  <div className="mb-2">
                    <span className="fw-bold text-white">
                      Negative Response:
                    </span>{' '}
                    <small className="text-light">
                      Complaints or unfavorable replies
                    </small>
                  </div>
                </Col>
                <Col md={4}>
                  <div className="mb-2">
                    <span className="fw-bold text-white">
                      Opt Out Response:
                    </span>{' '}
                    <small className="text-light">
                      STOP, UNSUBSCRIBE requests
                    </small>
                  </div>
                  <div className="mb-2">
                    <span className="fw-bold text-white">Contact Info:</span>{' '}
                    <small className="text-light">
                      Sharing contact details
                    </small>
                  </div>
                  <div className="mb-2">
                    <span className="fw-bold text-white">Conversational:</span>{' '}
                    <small className="text-light">
                      General conversation messages
                    </small>
                  </div>
                </Col>
              </Row>
              <Row className="g-2 mt-1">
                <Col md={4}>
                  <div>
                    <span className="fw-bold text-white">Mass Marketing:</span>{' '}
                    <small className="text-light">
                      Automated bulk messages
                    </small>
                  </div>
                </Col>
                <Col md={4}>
                  <div>
                    <span className="fw-bold text-white">Follow Up:</span>{' '}
                    <small className="text-light">
                      Follow-up communications
                    </small>
                  </div>
                </Col>
                <Col md={4}>
                  <div>
                    <span className="fw-bold text-white">Spam Report:</span>{' '}
                    <small className="text-light">Reported as spam</small>
                  </div>
                </Col>
              </Row>
            </Card.Body>
          </Card>

          {/* First Message Analytics */}
          <Card className="border border-secondary bg-secondary text-white mb-3">
            <Card.Header className="bg-secondary border-bottom border-secondary">
              <h6 className="text-light mb-0 fw-semibold">
                First Message Analytics
              </h6>
            </Card.Header>
            <Card.Body className="p-3">
              <Row className="g-2">
                <Col md={6}>
                  <div className="mb-2">
                    <span className="fw-bold text-white">
                      Avg Template Score:
                    </span>{' '}
                    <small className="text-light">
                      Template quality rating (0-100)
                    </small>
                  </div>
                  <div className="mb-2">
                    <span className="fw-bold text-white">
                      Mass Marketing Indicators:
                    </span>{' '}
                    <small className="text-light">
                      Count of spam-like first messages
                    </small>
                  </div>
                  <div className="mb-2">
                    <span className="fw-bold text-white">
                      Low Quality First Messages:
                    </span>{' '}
                    <small className="text-light">
                      Poor quality initial contacts
                    </small>
                  </div>
                  <div className="mb-2">
                    <span className="fw-bold text-white">
                      High Quality First Messages:
                    </span>{' '}
                    <small className="text-light">
                      Excellent initial contacts
                    </small>
                  </div>
                  <div>
                    <span className="fw-bold text-white">
                      Avg Personalization Score:
                    </span>{' '}
                    <small className="text-light">
                      How personalized messages are (0-100)
                    </small>
                  </div>
                </Col>
                <Col md={6}>
                  <div className="mb-2">
                    <span className="fw-bold text-white">
                      Total Template Score:
                    </span>{' '}
                    <small className="text-light">
                      Sum of all template scores
                    </small>
                  </div>
                  <div className="mb-2">
                    <span className="fw-bold text-white">
                      Avg Cold Outreach Quality:
                    </span>{' '}
                    <small className="text-light">
                      Quality of unsolicited messages
                    </small>
                  </div>
                  <div className="mb-2">
                    <span className="fw-bold text-white">
                      Total Personalization Score:
                    </span>{' '}
                    <small className="text-light">
                      Sum of personalization scores
                    </small>
                  </div>
                  <div className="mb-2">
                    <span className="fw-bold text-white">
                      Total First Messages:
                    </span>{' '}
                    <small className="text-light">
                      Count of initial outbound messages
                    </small>
                  </div>
                  <div className="mb-2">
                    <span className="fw-bold text-white">
                      First Message Response Rate:
                    </span>{' '}
                    <small className="text-light">
                      Percentage who replied to first message
                    </small>
                  </div>
                  <div>
                    <span className="fw-bold text-white">
                      Total Cold Outreach Score:
                    </span>{' '}
                    <small className="text-light">
                      Sum of cold outreach quality scores
                    </small>
                  </div>
                </Col>
              </Row>
            </Card.Body>
          </Card>

          {/* Core Metrics */}
          <Card className="border border-secondary bg-secondary text-white">
            <Card.Header className="bg-secondary border-bottom border-secondary">
              <h6 className="text-light mb-0 fw-semibold">Core Metrics</h6>
            </Card.Header>
            <Card.Body className="p-3">
              <Row className="g-2">
                <Col md={6}>
                  <div className="mb-2">
                    <span className="fw-bold text-white">Total Messages:</span>{' '}
                    <small className="text-light">
                      All messages (inbound + outbound)
                    </small>
                  </div>
                  <div className="mb-2">
                    <span className="fw-bold text-white">
                      Avg Quality Score:
                    </span>{' '}
                    <small className="text-light">
                      Message quality rating (0-100)
                    </small>
                  </div>
                  <div className="mb-2">
                    <span className="fw-bold text-white">
                      Avg Conversation Score:
                    </span>{' '}
                    <small className="text-light">
                      Relationship health (0-100)
                    </small>
                  </div>
                  <div>
                    <span className="fw-bold text-white">Quality Tier:</span>{' '}
                    <small className="text-light">
                      EXCELLENT, GOOD, FAIR, POOR, MASS_MARKETING
                    </small>
                  </div>
                </Col>
                <Col md={6}>
                  <div className="mb-2">
                    <span className="fw-bold text-white">
                      Overall Risk Score:
                    </span>{' '}
                    <small className="text-light">
                      Blocking risk assessment (0-100)
                    </small>
                  </div>
                  <div className="mb-2">
                    <span className="fw-bold text-white">
                      Avg Spam Likelihood:
                    </span>{' '}
                    <small className="text-light">
                      Probability messages are spam (0-100)
                    </small>
                  </div>
                  <div>
                    <span className="fw-bold text-white">Recipients:</span>{' '}
                    <small className="text-light">
                      Unique phone numbers contacted
                    </small>
                  </div>
                </Col>
              </Row>
            </Card.Body>
          </Card>
        </Modal.Body>
      </Modal>
    </>
  )
}

export default BehavioralProfileOverview
